<template>
  <!-- 🌿 Banner -->
  <section class="pt-0">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div
            class="card overflow-hidden h-100px h-xl-200px rounded-3"
            style="
              background-image: url(/assets/images/bg/fondo-acercade-viaverde.jpg);
              background-position: center left;
              background-size: cover;
            "
          >
            <div class="bg-overlay bg-dark opacity-6"></div>

            <div
              class="card-img-overlay d-flex align-items-end flex-column justify-content-end"
            >
              <div class="w-100 p-3 p-sm-4">
                <h2 class="text-white fw-semibold mb-1">
                  Publicaciones en Redes
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Estado: loading / error / contenido -->
  <div class="container my-4">
    <div v-if="socialPostsStore.loading" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
      <p class="mt-2">Cargando publicaciones...</p>
    </div>

    <div v-else-if="socialPostsStore.error" class="alert alert-danger">
      {{ socialPostsStore.error }}
    </div>

    <div v-else>
      <div class="social-wrapper">
        <!-- Encabezado -->
        <header>
          <div class="filtros">
            <button
              v-for="btn in filtros"
              :key="btn.value"
              :class="{ activo: filtroActivo === btn.value }"
              @click="setFiltro(btn.value)"
            >
              {{ btn.label }}
            </button>
          </div>
        </header>

        <!-- ✅ Blank state -->
        <section v-if="postsFiltrados.length === 0" class="blank-state">
          <div class="blank-icon">📭</div>
          <h4 class="blank-title">Aún no hay publicaciones</h4>
          <p class="blank-desc">
            Cuando existan publicaciones disponibles, las verás aquí.
          </p>
        </section>

        <!-- Grid de publicaciones -->
        <section v-else class="contenedor">
          <article
            v-for="post in postsFiltrados"
            :key="post.id"
            class="card"
            :data-category="post.type"
          >
            <!-- Cabecera -->
            <div v-if="post.type === 'facebook'" class="titulo-cabecera">
              <img
                src="assets/images/quienes-participan/icon-facebook.png"
                width="30"
                alt="Facebook"
              />
              <span class="badge">{{ post.tag }}</span>
            </div>

            <div v-else class="titulo-cabecera">
              <img
                src="assets/images/quienes-participan/icon-tiktok1.png"
                width="30"
                alt="TikTok"
              />
              <span class="badge">{{ post.tag }}</span>
            </div>

            <!-- ========================= -->
            <!-- Facebook Post Embed       -->
            <!-- ========================= -->
            <div v-if="post.type === 'facebook'">
              <iframe
                loading="lazy"
                :src="`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
                  post.url
                )}`"
                width="100%"
                height="450"
                frameborder="0"
                scrolling="no"
                style="border: none; overflow: hidden"
                allowfullscreen
              ></iframe>

              <a :href="post.url" target="_blank" class="btn-ver">
                🔗 Ver publicación original
              </a>
            </div>

            <!-- ========================= -->
            <!-- TikTok Preview + Click    -->
            <!-- ========================= -->
            <div v-if="post.type === 'tiktok'">
              <div
                class="tiktok-preview"
                v-if="!post.playing"
                @click="playTikTok(post)"
              >
                <img
                  v-if="post.thumbnail"
                  :src="post.thumbnail"
                  alt="TikTok preview"
                />
                <div v-else class="tiktok-fallback">
                  <span>Cargando vista previa...</span>
                </div>

                <button class="play-tiktok">Ver video ▶</button>
              </div>

              <div class="tiktok-frame" v-if="post.playing">
                <blockquote
                  class="tiktok-embed"
                  :data-video-id="post.videoId"
                  :cite="post.url"
                  style="max-width: 100%"
                >
                  <section></section>
                </blockquote>
              </div>

              <a :href="post.url" target="_blank" class="btn-ver">
                🔗 Ver publicación original
              </a>
            </div>
          </article>
        </section>

        <!-- 🔄 Paginación -->
        <nav
          v-if="socialPostsStore.totalPages > 1 && postsFiltrados.length > 0"
          class="mt-4 d-flex justify-content-center"
          aria-label="navigation"
        >
          <ul
            class="pagination pagination-primary-soft d-inline-block d-md-flex rounded mb-0"
          >
            <li
              class="page-item mb-0"
              :class="{ disabled: socialPostsStore.currentPage === 1 }"
            >
              <button
                class="page-link"
                @click="
                  socialPostsStore.changePage(socialPostsStore.currentPage - 1)
                "
              >
                <i class="fas fa-angle-double-left"></i>
              </button>
            </li>

            <li
              v-for="page in socialPostsStore.totalPages"
              :key="page"
              class="page-item mb-0"
              :class="{ active: page === socialPostsStore.currentPage }"
            >
              <button
                class="page-link"
                @click="socialPostsStore.changePage(page)"
              >
                {{ page }}
              </button>
            </li>

            <li
              class="page-item mb-0"
              :class="{
                disabled:
                  socialPostsStore.currentPage === socialPostsStore.totalPages,
              }"
            >
              <button
                class="page-link"
                @click="
                  socialPostsStore.changePage(socialPostsStore.currentPage + 1)
                "
              >
                <i class="fas fa-angle-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed, nextTick } from "vue";
import { useSocialPostsStore } from "@/modules/social-posts/stores/social-posts";

export default {
  name: "SocialPostsHomePage",

  setup() {
    const socialPostsStore = useSocialPostsStore();

    const filtroActivo = ref("all");
    const filtros = [
      { label: "Todos", value: "all" },
      { label: "Facebook", value: "facebook" },
      { label: "TikTok", value: "tiktok" },
    ];

    onMounted(() => {
      socialPostsStore.fetchSocialPosts();
    });

    const postsFiltrados = computed(() => {
      if (filtroActivo.value === "all") return socialPostsStore.posts;
      return socialPostsStore.posts.filter(
        (p) => p.type === filtroActivo.value
      );
    });

    const setFiltro = async (value) => {
      filtroActivo.value = value;
      await socialPostsStore.changePlatform(value);
    };

    const ensureTikTokScript = () => {
      if (
        document.querySelector('script[src="https://www.tiktok.com/embed.js"]')
      ) {
        return;
      }
      const script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      document.body.appendChild(script);
    };

    const playTikTok = async (post) => {
      post.playing = true;
      await nextTick();
      ensureTikTokScript();
    };

    return {
      socialPostsStore,
      filtros,
      filtroActivo,
      postsFiltrados,
      setFiltro,
      playTikTok,
    };
  },
};
</script>

<style scoped>
.social-wrapper {
  font-family: "Montserrat";
}

header {
  text-align: center;
  background: #fff;
  padding: 20px;
  border-bottom: 1px solid #ddd;
}

.filtros {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
}

.filtros button {
  padding: 8px 18px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  background: #e1e1e1;
  transition: 0.3s;
}

.filtros button.activo,
.filtros button:hover {
  background: #00a052;
  color: white;
}

.contenedor {
  width: 95%;
  max-width: 1100px;
  margin: 30px auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 25px;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
}

.card .titulo-cabecera {
  margin-bottom: 20px;
}

.tiktok-preview {
  position: relative;
  cursor: pointer;
}

.tiktok-preview img {
  width: 100%;
  border-radius: 10px;
}

.tiktok-fallback {
  width: 100%;
  height: 220px;
  border-radius: 10px;
  background: #f1f1f1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6b6b6b;
  font-size: 14px;
}

.play-tiktok {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 14px;
  font-size: 16px;
  background: rgba(0, 0, 0, 0.65);
  color: white;
  border: none;
  border-radius: 6px;
}

.btn-ver {
  display: block;
  text-align: center;
  padding: 8px;
  margin-top: 8px;
  border-radius: 6px;
  background: #00a052;
  color: white;
  text-decoration: none;
}

.badge {
  float: right;
  padding: 5px 10px 0px 10px;
  border-radius: 10px;
  font-size: 12px;
  color: #5c5c5c;
  font-weight: 600;
}

.badge::before {
  content: "• ";
  color: #00a052;
  font-weight: bold;
  font-size: 16px;
}

/* ✅ Blank state */
.blank-state {
  max-width: 900px;
  margin: 35px auto 0;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 28px 18px;
  text-align: center;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.05);
}

.blank-icon {
  font-size: 42px;
  margin-bottom: 10px;
}

.blank-title {
  font-family: "Merriweather";
  margin: 0;
  font-size: 20px;
}

.blank-desc {
  margin: 8px 0 0;
  color: #6b6b6b;
  font-size: 14px;
}
</style>
